import React, { BaseSyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import BlueFullWidthButton from 'components/BlueFullWidthButton/BlueFullWidthButton';

import EntitySelectDialog from '../../../components/EntitySelectDialog/EntitySelectDialog';
import ChangePasswordDialog from '../../../dialogs/ChangePasswordDialog/ChangePasswordDialog';
import { ReduxState } from '../../../redux/types';
import initialState from '../../../redux/user/initialState';
import { setCompanyId, setFromApi } from '../../../redux/user/userSlice';
import routes from '../../../routes';
import api from '../../../utils/api';
import LanguageSelectorDrawer from '../LanguageSelectorDrawer/LanguageSelectorDrawer';
import ProfileInfoDrawer from '../ProfileInfoDrawer/ProfileInfoDrawer';
import { Container, Content, Footer, ProfileInfoContainer, ProfileMenuButton, StyledDivider } from './styled';

/*
 * This component renders the content of the drawer (sidebar).
 */

interface Props {
    closeDrawer: () => void;
}

const DrawerContent = (props: Props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { closeDrawer } = props;

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
    const [isEntitySelectDialogOpen, setIsEntitySelectDialogOpen] = useState(false);

    const { t } = useTranslation();

    const userInfo = useSelector((state: ReduxState) => state.user);

    const selectedCompanyId = useMemo(() => {
        return userInfo.companyId;
    }, [userInfo]);

    const userRole = useMemo(() => {
        return userInfo.type;
    }, [userInfo]);

    const isLoggedIn = useMemo(() => {
        return userInfo.id > 0;
    }, [userInfo]);

    const companies = useMemo(() => {
        return userInfo.enabledCompanies;
    }, [userInfo]);

    const companiesForEntitySelectDialog = useMemo(() => {
        return userInfo.enabledCompanies.map(c => {
            return {
                id: c.id,
                name: c.name,
                description: c.vatCode ? c.vatCode : undefined
            };
        });
    }, [userInfo]);

    const openProfileMenu = (e: BaseSyntheticEvent) => {
        setMenuAnchor(e.currentTarget);
    };

    const closeProfileMenu = () => {
        setMenuAnchor(null);
    };

    const handleChangePassword = useCallback(() => {
        setIsChangePasswordDialogOpen(true);
        closeDrawer();
        closeProfileMenu();
    }, [closeDrawer]);

    const handleLogout = useCallback(() => {
        closeProfileMenu();
        api.logout();
        dispatch(setFromApi(initialState));
    }, [dispatch]);

    const handleCompanySelect = useCallback((companyId?: number) => {
        if (companyId) {
            dispatch(setCompanyId(companyId));
            localStorage.setItem('company_id', companyId.toString());
            history.push('/');
        }

        setIsEntitySelectDialogOpen(false);
    }, [dispatch, history]);

    const items = useMemo(() => {
        // items to be rendered in the sidebar
        return routes.map((route) => {
            if (!route.showInSidebar ||
                (!isLoggedIn && route.requireAuth) ||
                (route.roles && !route.roles.includes(userRole)) ||
                (isLoggedIn && !route.canAuth)
            ) {
                // returns null if the user is not authorized to access the page, or the page is set to be hidden in the sidebar
                return null;
            }

            const exactPath = route.exactPathSidebar && route.exactPath;

            return (
                <ListItem button key={route.path} to={route.path} component={NavLink} exact={exactPath} activeClassName='Mui-selected' onClick={closeDrawer} >
                    {userRole === 'customer' && (
                        <>
                            <div style={{ margin: '4px 12px 4px 8px', flexShrink: 0, height: '54px', width: '54px', backgroundImage: 'url("' + route.image + '")', backgroundRepeat: 'no-repeat', backgroundSize: 'contain', backgroundPosition: 'center' }}>

                            </div>
                            {/*
                                <img src={route.image ?? ''} style={{height: '80px', width: '80px', margin: '22px'}} />
                            */}
                        </>

                    )}
                    <ListItemText primary={t(route.name ?? '')} />
                </ListItem>
            );
        }).filter((route) => route !== null);
    }, [closeDrawer, isLoggedIn, t, userRole]);

    return (
        <Container>
            <Content>
                <ProfileInfoContainer>
                    <ProfileInfoDrawer />

                    <ProfileMenuButton edge='start' color='inherit' onClick={openProfileMenu} >
                        <ExpandMoreIcon />
                    </ProfileMenuButton>

                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeProfileMenu} keepMounted >
                        <MenuItem onClick={handleChangePassword}>
                            {t('generic.change_password')}
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            {t('generic.logout')}
                        </MenuItem>
                    </Menu>
                </ProfileInfoContainer>

                <StyledDivider variant='middle' />

                {userRole === 'customer' && companies.length > 1 && (
                    <>
                        <Paper elevation={0} style={{ margin: '0px', padding: '8px', textAlign: 'center', color: '#194560', fontSize: '1.1em' }}>
                            <p style={{ margin: 0 }}>
                                {t('drawer.selected_company')}
                            </p>
                            <p style={{ margin: '4px 0 12px', color: '#194560', fontWeight: 500, fontSize: '1.25em' }}>
                                {companies?.find(c => c.id === selectedCompanyId)?.name ?? 'n.a.'}
                            </p>
                            <BlueFullWidthButton onClick={() => setIsEntitySelectDialogOpen(true)} color='primary' variant='outlined' disabled={false} style={{ width: 'calc(100% + 17px)', display: 'block', margin: 'auto -9px' }}>
                                {t('drawer.change_company')}
                            </BlueFullWidthButton>
                        </Paper>

                        <StyledDivider variant='middle' />
                    </>
                )}

                <List>
                    {items}
                </List>
            </Content>

            <Footer>
                <LanguageSelectorDrawer />
            </Footer>

            <ChangePasswordDialog open={isChangePasswordDialogOpen} onClose={() => setIsChangePasswordDialogOpen(false)} />
            <EntitySelectDialog open={isEntitySelectDialogOpen} title={t('drawer.change_company')} data={companiesForEntitySelectDialog} onClose={handleCompanySelect} />
        </Container>
    );
};

export default DrawerContent;
